import { useState, useEffect } from 'react';
import { useServiceEnquiry } from '../../../hooks/useServiceEnquiry';
import { BiReset } from "react-icons/bi";

export const ServiceEnq = () => {
  const {
    years,
    getAllEnquiries,
    SERVICE_ITEMS,
    isLoading
  } = useServiceEnquiry();

  const [selectedYear, setSelectedYear] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedService, setSelectedService] = useState('');
  const [enquiries, setEnquiries] = useState([]);
  const [isFetching, setIsFetching] = useState(false);

  const MONTH_NAMES = [
    { value: 'jan', label: 'January' },
    { value: 'feb', label: 'February' },
    { value: 'mar', label: 'March' },
    { value: 'apr', label: 'April' },
    { value: 'may', label: 'May' },
    { value: 'jun', label: 'June' },
    { value: 'jul', label: 'July' },
    { value: 'aug', label: 'August' },
    { value: 'sep', label: 'September' },
    { value: 'oct', label: 'October' },
    { value: 'nov', label: 'November' },
    { value: 'dec', label: 'December' }
  ];

  // Set default year on load
  useEffect(() => {
    if (years.length > 0 && !selectedYear) {
      setSelectedYear(years[0]);
    }
  }, [years, selectedYear]);

  // Fetch enquiries
  const fetchEnquiries = async () => {
    if (!selectedYear) return;

    setIsFetching(true);
    try {
      const filters: any = { year: selectedYear };
      if (selectedMonth) {
        filters.month = selectedMonth;
      }
      if (selectedService) {
        filters.interestItem = selectedService;
      }

      const data: any = await getAllEnquiries(filters);
      setEnquiries(data);
    } catch (error) {
      console.error('Error fetching service enquiries:', error);
      setEnquiries([]);
    } finally {
      setIsFetching(false);
    }
  };

  // Fetch data on filter changes
  useEffect(() => {
    fetchEnquiries();
  }, [selectedYear, selectedMonth, selectedService]);

  const handleReset = () => {
    setSelectedYear(years[0] || '');
    setSelectedMonth('');
    setSelectedService('');
  };

  // Shorten long service names for display
  const formatServiceName = (serviceName: string) => {
    const shortNames: Record<string, string> = {
      "Glovebox Inspection & Preventive Maintenance": "Glovebox Inspection",
      "Vacuum & Inert Gas System Servicing": "Vacuum & Gas System",
      "Filter & Purification Media Replacement": "Filter Replacement",
      "Seal, Gasket & O-Ring Replacement": "Seal & Gasket",
      "Sensor Cleaning & Calibration": "Sensor Calibration",
      "Customization & Technical Support": "Technical Support"
    };
    
    return shortNames[serviceName] || serviceName;
  };

  return (
    <div className="overflow-x-auto">
      {/* Header with filters */}
      <div className="">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
          <div>
            <h2 className="text-[36px] font-bold font-barlow text-gray-700">Service Enquiries</h2>
            <p className="text-sm text-gray-600 mt-1 font-roboto">
              {selectedYear} 
              {selectedMonth ? ` - ${MONTH_NAMES.find(m => m.value === selectedMonth)?.label}` : ' • All Months'}
              {selectedService ? ` - ${formatServiceName(selectedService)}` : ''}
              {enquiries.length > 0 && ` • ${enquiries.length} enquiries`}
            </p>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            {/* Year Filter */}
            <div className="relative min-w-[120px]">
              <label htmlFor="year-select" className="block text-sm font-medium text-gray-700 mb-1">
                Year
              </label>
              <div className="relative">
                <select
                  id="year-select"
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg shadow-sm focus:outline-none bg-white text-gray-900 appearance-none"
                  disabled={isLoading}
                >
                  {years.length === 0 ? (
                    <option value="">No data available</option>
                  ) : (
                    years.map((year: any) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))
                  )}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Month Filter */}
            <div className="relative min-w-[140px]">
              <label htmlFor="month-select" className="block text-sm font-medium text-gray-700 mb-1">
                Month
              </label>
              <div className="relative">
                <select
                  id="month-select"
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg shadow-sm focus:outline-none bg-white text-gray-900 appearance-none"
                >
                  <option value="">All Months</option>
                  {MONTH_NAMES.map((month) => (
                    <option key={month.value} value={month.value}>
                      {month.label}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Service Filter */}
            <div className="relative min-w-[200px]">
              <label htmlFor="service-select" className="block text-sm font-medium text-gray-700 mb-1">
                Service
              </label>
              <div className="relative">
                <select
                  id="service-select"
                  value={selectedService}
                  onChange={(e) => setSelectedService(e.target.value)}
                  className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg shadow-sm focus:outline-none bg-white text-gray-900 appearance-none"
                >
                  <option value="">All Services</option>
                  {SERVICE_ITEMS.map((service) => (
                    <option key={service} value={service}>
                      {formatServiceName(service)}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Reset Button */}
            <div className="self-end">
              <button
                onClick={handleReset}
                disabled={isFetching}
                className="flex items-center gap-2 px-4 py-2.5 bg-gray-700 text-white rounded-lg hover:bg-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isFetching ? (
                  <>
                    <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Loading...
                  </>
                ) : (
                  <div className='flex items-center gap-2'>
                    <BiReset size={20}/>
                    Reset
                  </div>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Table Container */}
      <div className='border-[1px] rounded-lg'>
        <div className="overflow-x-auto shadow rounded-lg">
          {isFetching ? (
            <div className="flex justify-center items-center py-12">
              <div className="text-center">
                <svg className="animate-spin h-8 w-8 text-gray-500 mx-auto mb-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                <p className="text-gray-600">Loading service enquiries...</p>
              </div>
            </div>
          ) : enquiries.length === 0 ? (
            <div className="text-center py-12">
              <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No service enquiries found</h3>
              <p className="text-gray-500">
                {selectedYear && (selectedMonth || selectedService)
                  ? `No service enquiries found for the selected filters`
                  : selectedYear
                    ? `No service enquiries found for ${selectedYear}`
                    : 'Select a year to view service enquiries'
                }
              </p>
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-500">
                <tr className='text-white font-roboto uppercase text-start tracking-wider'>
                  <th className="px-6 py-3 text-start">Sl No</th>
                  <th className="px-6 py-3 text-start">Name</th>
                  <th className="px-6 py-3 text-start">Mobile</th>
                  <th className="px-6 py-3 text-start">Email</th>
                  <th className="px-6 py-3 text-start">Service</th>
                  <th className="px-6 py-3 text-start">Date</th>
                  <th className="px-6 py-3 text-start">Message</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {enquiries.map((enquiry: any, index) => (
                  <tr key={enquiry.id || index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{index + 1}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                      {enquiry.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <a href={`tel:${enquiry.mobile}`} className="hover:text-blue-600">
                        {enquiry.mobile}
                      </a>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <a href={`mailto:${enquiry.email}`} className="text-blue-600 hover:text-blue-900 hover:underline">
                        {enquiry.email}
                      </a>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                        {formatServiceName(enquiry.interestItem)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {enquiry.timestamp ? new Date(enquiry.timestamp).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 max-w-xs">
                      <div className="truncate" title={enquiry.message}>
                        {enquiry.message}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination info */}
        {enquiries.length > 0 && (
          <div className="px-6 py-4 border-t rounded-b-lg bg-gray-500 text-white font-roboto">
            <div className="flex items-center justify-between font-semibold tracking-wide">
              <div className="text-sm">
                Showing <span className="font-medium">1</span> to{' '}
                <span className="font-medium">{enquiries.length}</span> of{' '}
                <span className="font-medium">{enquiries.length}</span> enquiries
              </div>
              <div className="text-sm">
                {selectedYear}
                {selectedMonth && ` • ${MONTH_NAMES.find(m => m.value === selectedMonth)?.label}`}
                {selectedService && ` • ${formatServiceName(selectedService)}`}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};